import { SwapHoriz } from '@mui/icons-material'
import { Box, Button, Container, Link, Typography } from '@mui/material'
import React from 'react'

import LazyImage from '@/src/components/common/ui/LazyImage'

interface Route {
    origin: string
    destination: string
    href: string
    fromCode?: string
    toCode?: string
}

interface CityRoute {
    id: string
    destination: string
    imageUrl: string
    routes: Route[]
}

interface FamilyDestination {
    city: string
    country: string
    href: string
}

type TranslationFunction = (key: string) => string

interface TopCitiesAndRoutesProps {
    title: string
    routes: CityRoute[]
    familyTrips: {
        bannerImage: string
        title: string
        subtitle: string
        subtitleSuffix: string
        overlayTitle: string
        overlayDescription: string
        bookNowHref: string
        destinations: FamilyDestination[]
    }
    onBookClick: (href: string, origin?: string, destination?: string) => void
    onPrefillFromHref?: (href: string) => boolean
    t: TranslationFunction
}

const CityRouteCard: React.FC<{
    cityRoute: CityRoute
    onBookClick: (href: string, origin?: string, destination?: string, fromCode?: string, toCode?: string) => void
    isLast?: boolean
    t: TranslationFunction
}> = ({ cityRoute, onBookClick, isLast = false, t }) => {
    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    overflow: 'hidden',
                    mb: isLast ? 0 : 2,
                    boxShadow: theme =>
                        `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0,0,0,0.08)'}`
                }}
            >
                <Box
                    sx={{
                        flex: { xs: '0 0 120px', md: '0 0 160px' },
                        position: 'relative',
                        minHeight: { xs: '160px', md: '220px' }
                    }}
                >
                    <LazyImage
                        src={cityRoute.imageUrl}
                        alt={cityRoute.destination}
                        sizes="(max-width: 768px) 120px, 160px"
                        objectFit="cover"
                    />
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        p: { xs: 1.5, md: 2 }
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 600,
                            mb: { xs: 1.5, md: 2 },
                            color: 'text.primary',
                            fontSize: { xs: '0.875rem', md: '1rem' }
                        }}
                    >
                        {t('roundTripsTo')} {cityRoute.destination}
                    </Typography>
                    <Box
                        component="ul"
                        sx={{ display: 'flex', flexDirection: 'column', gap: 0, m: 0, p: 0, listStyle: 'none' }}
                    >
                        {cityRoute.routes.map((route, index) => (
                            <Box key={index} component="li">
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        py: 1.25,
                                        borderBottom: index < cityRoute.routes.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider'
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
                                        {route.origin}
                                    </Typography>
                                    <SwapHoriz sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                                    <Typography sx={{ fontWeight: 500, color: 'text.primary', fontSize: '0.875rem' }}>
                                        {route.destination}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() =>
                                            onBookClick(
                                                route.href,
                                                route.origin,
                                                route.destination,
                                                route.fromCode,
                                                route.toCode
                                            )
                                        }
                                        sx={{
                                            ml: 'auto',
                                            bgcolor: 'primary.main',
                                            color: 'common.white',
                                            textTransform: 'none',
                                            px: 1.5,
                                            py: 0.5,
                                            fontSize: '0.6875rem',
                                            minWidth: '55px',
                                            '&:hover': {
                                                bgcolor: 'primary.dark'
                                            }
                                        }}
                                    >
                                        {t('book')}
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </>
    )
}

const MobileFamilyDestinations: React.FC<{
    familyTrips: TopCitiesAndRoutesProps['familyTrips']
    t: TranslationFunction
    onPrefillFromHref?: (href: string) => boolean
}> = ({ familyTrips, t, onPrefillFromHref }) => {
    return (
        <Box
            sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                p: 2,
                boxShadow: theme =>
                    `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0,0,0,0.08)'}`
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: 'text.primary',
                    fontSize: '1rem',
                    textAlign: 'center'
                }}
            >
                {t('topFamilyDestinations')}
            </Typography>
            <Box
                component="ul"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 0,
                    m: 0,
                    p: 0,
                    listStyle: 'none'
                }}
            >
                {familyTrips.destinations.map((dest, index) => (
                    <Box
                        component="li"
                        key={index}
                        sx={{
                            borderBottom: index < familyTrips.destinations.length - 2 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            py: 1.25
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    fontSize: '1rem',
                                    color: 'text.secondary',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                ✈
                            </Box>
                            <Typography component="span" sx={{ fontSize: '0.875rem', lineHeight: 1.4, color: 'text.secondary' }}>
                                {dest.city} -{' '}
                            </Typography>
                            <Link
                                href={dest.href}
                                onClick={event => {
                                    if (onPrefillFromHref?.(dest.href)) {
                                        event.preventDefault()
                                    }
                                }}
                                sx={{
                                    fontSize: '0.875rem',
                                    lineHeight: 1.4,
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                {dest.country}
                            </Link>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

const MobileView: React.FC<{
    title: string
    routes: CityRoute[]
    familyTrips: TopCitiesAndRoutesProps['familyTrips']
    onBookClick: (href: string, origin?: string, destination?: string, fromCode?: string, toCode?: string) => void
    t: TranslationFunction
    onPrefillFromHref?: (href: string) => boolean
}> = ({ title, routes, familyTrips, onBookClick, t, onPrefillFromHref }) => {
    // Show only first 3 routes on mobile
    const mobileRoutes = routes.slice(0, 3)

    return (
        <Box sx={{ px: 2 }}>
            <Typography
                variant="h5"
                sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: 'text.primary',
                    fontSize: '1.25rem'
                }}
            >
                {title}
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0
                }}
            >
                {/* First 3 City Route Cards */}
                {mobileRoutes.map((cityRoute, index) => (
                    <CityRouteCard
                        key={cityRoute.id}
                        cityRoute={cityRoute}
                        onBookClick={onBookClick}
                        isLast={false}
                        t={t}
                    />
                ))}

                {/* Top Family Destinations (4th item) */}
                <MobileFamilyDestinations familyTrips={familyTrips} t={t} onPrefillFromHref={onPrefillFromHref} />
            </Box>
        </Box>
    )
}

const FamilyTripsSection: React.FC<{
    familyTrips: TopCitiesAndRoutesProps['familyTrips']
    t: TranslationFunction
    onPrefillFromHref?: (href: string) => boolean
}> = ({ familyTrips, t, onPrefillFromHref }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: '690px',
                gap: 2
            }}
        >
            {/* Family Trips Banner */}
            <Box
                sx={{
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    height: '380px',
                    mb: 0,
                    flex: 1
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 1
                    }}
                >
                    <LazyImage
                        src={familyTrips.bannerImage}
                        alt="Family Trips"
                        sizes="(min-width: 1200px) 50vw, 100vw"
                        objectFit="cover"
                    />
                </Box>

                {/* Yellow Banner - Top Left */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 16,
                        bgcolor: 'secondary.main',
                        zIndex: 2,
                        p: 2.5,
                        borderRadius: '0 0px 12px 12px',
                        maxWidth: '280px'
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.9375rem',
                            fontWeight: 500,
                            color: 'common.black',
                            mb: 0.5,
                            lineHeight: 1.2
                        }}
                    >
                        {t('fabulous')}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '2rem',
                            fontWeight: 700,
                            color: 'common.black',
                            lineHeight: 1.2
                        }}
                    >
                        {t('family')}{' '}
                        <Box component="span" sx={{ fontWeight: 400 }}>
                            {t('trips')}
                        </Box>
                    </Typography>
                </Box>

                {/* Blue Overlay - Bottom Right */}
                <Box
                    sx={theme => ({
                        position: 'absolute',
                        bottom: 0,
                        right: 16,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(25, 77, 145, 0.95)',
                        zIndex: 2,
                        p: 3,
                        maxWidth: '320px',
                        borderRadius: '12px 12px 0px 0px'
                    })}
                >
                    <Typography
                        sx={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: 'secondary.main',
                            mb: 1.5,
                            lineHeight: 1.2
                        }}
                    >
                        {t('flyTogether')}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '0.875rem',
                            color: 'common.white',
                            mb: 2.5,
                            lineHeight: 1.6
                        }}
                    >
                        {t('bookCheapFlightTicketsForFamily')}
                    </Typography>
                    <Button
                        variant="contained"
                        href={familyTrips.bookNowHref}
                        sx={{
                            bgcolor: 'secondary.main',
                            color: 'common.black',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            px: 3,
                            py: 1.25,
                            fontSize: '0.875rem',
                            borderRadius: 2,
                            boxShadow: theme =>
                                `0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0,0,0,0.2)'}`,
                            '&:hover': {
                                bgcolor: 'secondary.dark',
                                boxShadow: theme =>
                                    `0 4px 8px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0,0,0,0.3)'}`
                            }
                        }}
                    >
                        {t('bookNow')}
                    </Button>
                </Box>
            </Box>

            {/* Top Family Destinations */}
            <Box
                sx={{
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    p: 2,
                    boxShadow: theme =>
                        `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0,0,0,0.08)'}`
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: 'text.primary',
                        fontSize: '1rem',
                        textAlign: 'center'
                    }}
                >
                    {t('topFamilyDestinations')}
                </Typography>
                <Box
                    component="ul"
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 0,
                        m: 0,
                        p: 0,
                        listStyle: 'none'
                    }}
                >
                    {familyTrips.destinations.map((dest, index) => (
                        <Box
                            component="li"
                            key={index}
                            sx={{
                                borderBottom: index < familyTrips.destinations.length - 2 ? '1px solid' : 'none',
                                borderColor: 'divider',
                                py: 1.25
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '1rem',
                                        color: 'text.secondary',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    ✈
                                </Box>
                                <Typography component="span" sx={{ fontSize: '0.875rem', lineHeight: 1.4, color: 'text.secondary' }}>
                                    {dest.city} -{' '}
                                </Typography>
                                <Link
                                    href={dest.href}
                                    onClick={event => {
                                        if (onPrefillFromHref?.(dest.href)) {
                                            event.preventDefault()
                                        }
                                    }}
                                    sx={{
                                        fontSize: '0.875rem',
                                        lineHeight: 1.4,
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    {dest.country}
                                </Link>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    )
}

export const TopCitiesAndRoutes: React.FC<TopCitiesAndRoutesProps> = ({
    title,
    routes,
    familyTrips,
    onBookClick,
    onPrefillFromHref,
    t
}) => {
    return (
        <Box
            sx={{
                py: { xs: 3, md: 4 }
                // bgcolor: 'grey.50'
            }}
        >
            {/* Mobile View */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <MobileView
                    title={title}
                    routes={routes}
                    familyTrips={familyTrips}
                    onBookClick={onBookClick}
                    t={t}
                    onPrefillFromHref={onPrefillFromHref}
                />
            </Box>

            {/* Desktop View */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 600,
                            mb: 4,
                            color: 'text.primary',
                            fontSize: '1.75rem'
                        }}
                    >
                        {title}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 3,
                            alignItems: 'stretch'
                        }}
                    >
                        {/* Left: Top Cities and Routes - Stacked Vertically */}
                        <Box
                            sx={{
                                flex: '0 0 50%',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: '700px'
                            }}
                        >
                            {routes.map((cityRoute, index) => (
                                <CityRouteCard
                                    key={cityRoute.id}
                                    cityRoute={cityRoute}
                                    onBookClick={onBookClick}
                                    isLast={index === routes.length - 1}
                                    t={t}
                                />
                            ))}
                        </Box>

                        {/* Right: Family Trips Section */}
                        <Box
                            sx={{
                                flex: '0 0 50%',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: '700px'
                            }}
                        >
                            <FamilyTripsSection familyTrips={familyTrips} t={t} onPrefillFromHref={onPrefillFromHref} />
                        </Box>
                    </Box>
                </Container>
            </Box>
        </Box>
    )
}

export type { TopCitiesAndRoutesProps }
